import { DogIcon } from '$components'
import { FormFieldGroup, FormGroup, FormLabel, FormSelect, FormInput, Text, Button } from '$components'
import { DogBreed } from '$api-client/types.gen'
import { useQuery } from '@tanstack/react-query'
import { petIndexOptions } from '$api-client/@tanstack/react-query.gen'
import { useFormContext } from 'react-hook-form'
import { CreateAppointmentSchema, Input, useCreateAppointmentForm } from '.'
import { useState } from 'react'
import { useInfiniteSizeCategoryOptions } from '$hooks/use-infinite-size-category-options'
import { useInfiniteCoatTypeOptions } from '$hooks/use-infinite-coat-type-options'
import { PetOptionSettingsLink } from '$components'

export function PetSection() {
  const { setValue, watch } = useFormContext<CreateAppointmentSchema>()
  const { clientId } = useCreateAppointmentForm()
  const [breedSearch, setBreedSearch] = useState<string>('')
  const { options: sizeOptions, search: sizeSearch } = useInfiniteSizeCategoryOptions()
  const { options: coatOptions, search: coatSearch } = useInfiniteCoatTypeOptions()

  const createNewClient = watch(Input._NewClient)
  const createNewPet = watch(Input._NewPet)

  const { data: pets } = useQuery({
    ...petIndexOptions(),
    queryKey: petIndexOptions({
      query: {
        parent_id: clientId,
      },
    }).queryKey,
    enabled: !!clientId,
  })

  return (
    <FormGroup label="Pet Information">
      {createNewPet && (
        <div className="flex items-center justify-between">
          <Text
            as="h4"
            type="s"
            weight="medium"
          >
            Create New Pet
          </Text>

          {!createNewClient && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setValue(Input._NewPet, false)
                setValue(Input.Pet, null!)
              }}
              disabled={!clientId}
            >
              Select Existing
            </Button>
          )}
        </div>
      )}

      {createNewPet && (
        <div className="space-y-stack-roomy p-stack-section bg-surface-muted rounded-card-sm">
          <FormFieldGroup>
            <FormLabel htmlFor={Input.NewPetName}>
              Pet Name
            </FormLabel>

            <FormInput
              name={Input.NewPetName}
              placeholder="Pet name"
            />
          </FormFieldGroup>

          <FormFieldGroup>
            <FormLabel htmlFor={Input.NewPetBreed}>
              Breed
            </FormLabel>

            <FormSelect
              name={Input.NewPetBreed}
              placeholder="Select breed..."
              clearable
              search={{
                search: breedSearch,
                onSearchChange: setBreedSearch,
              }}
              options={Object.entries(DogBreed).map(([id, value]) => ({ id, value }))}
            />
          </FormFieldGroup>

          <FormFieldGroup>
            <FormLabel htmlFor={Input.NewPetSizeCategory}>
              Size Category
            </FormLabel>

            <FormSelect
              name={Input.NewPetSizeCategory}
              placeholder="Select size..."
              options={sizeOptions}
              search={sizeSearch}
            />

            <PetOptionSettingsLink type="size" />
          </FormFieldGroup>

          <FormFieldGroup>
            <FormLabel htmlFor={Input.NewPetCoatType}>
              Coat Type
            </FormLabel>

            <FormSelect
              name={Input.NewPetCoatType}
              placeholder="Select coat type..."
              options={coatOptions}
              search={coatSearch}
            />

            <PetOptionSettingsLink type="coat" />
          </FormFieldGroup>
        </div>
      )}

      {!createNewPet && (
        <FormFieldGroup className="flex-1">
          <FormLabel htmlFor={Input.Pet}>
            Pet
          </FormLabel>

          <div className="flex items-center gap-stack-roomy">
            <FormSelect
              name={Input.Pet}
              placeholder="Select a pet"
              icon={DogIcon}
              options={pets?.data.map(p => ({ id: p.id.toString(), value: p.name })) ?? []}
              disabled={!clientId && !createNewClient}
            />

            {!createNewClient && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setValue(Input._NewPet, true)
                  setValue(Input.Pet, null!)
                }}
                disabled={!clientId}
              >
                New Pet
              </Button>
            )}
          </div>
        </FormFieldGroup>
      )}
    </FormGroup>
  )
}
